import { push } from 'connected-react-router';
import { Dispatch } from 'redux';
import { AppState } from '..';
import { IPlaylist, PlaylistActionResult } from './types';
import { playlistApi } from './api';
import { getPlaylistErrors } from './validation';

const normalizeItems = (playlist: IPlaylist): IPlaylist => ({
  ...playlist,
  items: playlist.items.map((item, index) => ({
    ...item,
    position: index,
  })),
});

interface ISavePlaylistOptions {
  navigate?: boolean;
}

export const createPlaylistDraft = (): IPlaylist => ({
  _id: `draft_${Date.now()}`,
  name: '',
  active: true,
  allPlayers: true,
  players: [],
  groups: [],
  items: [],
});

export const fetchPlaylists = (): PlaylistActionResult<void> => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: 'PLAYLISTS/FETCH_ALL_REQUEST' });

    try {
      const playlists = await playlistApi.fetchAll();
      dispatch({ type: 'PLAYLISTS/FETCH_ALL_SUCCESS', data: playlists });
    } catch (error) {
      dispatch({ type: 'PLAYLISTS/FETCH_ALL_FAILURE', error });
    }
  };
};

export const fetchPlaylist = (id: string): PlaylistActionResult<void> => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: 'PLAYLISTS/FETCH_REQUEST' });

    try {
      const playlist = await playlistApi.fetchOne(id);
      dispatch({ type: 'PLAYLISTS/FETCH_SUCCESS', data: playlist });
    } catch (error) {
      dispatch({ type: 'PLAYLISTS/FETCH_FAILURE', error });
    }
  };
};

export const addPlaylistDraft = (): PlaylistActionResult<IPlaylist> => {
  return (dispatch: Dispatch) => {
    const playlist = createPlaylistDraft();
    dispatch({ type: 'PLAYLISTS/ADD_DRAFT', data: playlist });
    dispatch(push(`/playlists/${playlist._id}`));

    return playlist;
  };
};

export const updatePlaylistDraft = (
  id: string,
  update: Partial<IPlaylist>
) => ({
  type: 'PLAYLISTS/UPDATE_DRAFT' as 'PLAYLISTS/UPDATE_DRAFT',
  id,
  update,
});

export const savePlaylist = (
  id: string,
  options: ISavePlaylistOptions = {}
): PlaylistActionResult<Promise<IPlaylist | void>> => {
  return async (dispatch: Dispatch, getState: () => AppState) => {
    const playlist = getState().playlists.all.find(item => item._id === id);

    if (!playlist) {
      return;
    }

    return savePlaylistData(playlist, options)(dispatch, getState, undefined);
  };
};

export const savePlaylistData = (
  playlist: IPlaylist,
  options: ISavePlaylistOptions = {}
): PlaylistActionResult<Promise<IPlaylist | void>> => {
  return async (dispatch: Dispatch, getState: () => AppState) => {
    const { navigate = true } = options;
    const normalized = normalizeItems(playlist);
    const errors = getPlaylistErrors(normalized, getState().creativities.all);

    if (errors.length) {
      dispatch({ type: 'PLAYLISTS/SAVE_FAILURE', error: errors });
      return;
    }

    dispatch({ type: 'PLAYLISTS/SAVE_REQUEST' });

    try {
      const isDraft = normalized._id.startsWith('draft_');
      const saved = await playlistApi.save({
        ...normalized,
        _id: isDraft ? '' : normalized._id,
      });
      dispatch({
        type: 'PLAYLISTS/SAVE_SUCCESS',
        data: saved,
        draftId: isDraft ? normalized._id : undefined,
      });
      if (navigate) {
        dispatch(push(isDraft ? '/playlists' : `/playlists/${saved._id}`));
      }

      return saved;
    } catch (error) {
      dispatch({ type: 'PLAYLISTS/SAVE_FAILURE', error });
    }
  };
};

export const deletePlaylist = (id: string): PlaylistActionResult<void> => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: 'PLAYLISTS/DELETE_REQUEST' });

    try {
      if (!id.startsWith('draft_')) {
        await playlistApi.delete(id);
      }
      dispatch({ type: 'PLAYLISTS/DELETE_SUCCESS', id });
      dispatch(push('/playlists'));
    } catch (error) {
      dispatch({ type: 'PLAYLISTS/DELETE_FAILURE', error });
    }
  };
};
